import React, { useState, useEffect } from 'react';
import { phoneScreeningsAPI } from '../../utils/api';
import { Drawer } from '../shared/DrawerManager';
import toastr from 'toastr';

const PhoneScreeningConduct = ({ screening, onClose, onCompleted }) => {
  const [checklist, setChecklist] = useState([]);
  const [notes, setNotes] = useState('');
  const [outcome, setOutcome] = useState('pending');
  const [rating, setRating] = useState(null);
  const [decision, setDecision] = useState('pending');
  const [nextStatus, setNextStatus] = useState('assessment'); // Default next stage
  
  const validNextStatuses = [
    { value: 'assessment_sent', label: 'Send Assessment' },
    { value: 'interview', label: 'Schedule Interview' },
    { value: 'shortlisted', label: 'Shortlist' },
    { value: 'rejected', label: 'Reject' },
  ];
  const [rejectionReason, setRejectionReason] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (screening) {
      setChecklist(screening.checklist || []);
      setNotes(screening.notes || '');
      setOutcome(screening.outcome || 'pending');
      setRating(screening.rating || null);
      setDecision(screening.decision || 'pending');
      setRejectionReason(screening.rejectionReason || '');
    }
  }, [screening]);

  const updateChecklistItem = (index, field, value) => {
    const updated = [...checklist];
    updated[index] = { ...updated[index], [field]: value };
    setChecklist(updated);
  };

  const handleComplete = async () => {
    if (decision === 'reject' && !rejectionReason.trim()) {
      toastr.error('Please provide a reason for rejection');
      return;
    }

    setSubmitting(true);
    try {
      await phoneScreeningsAPI.complete(screening._id, {
        checklist,
        notes,
        outcome,
        rating,
        decision,
        nextStatus: decision === 'move_to_next_stage' ? nextStatus : undefined,
        rejectionReason: decision === 'reject' ? rejectionReason : ''
      });

      toastr.success('Phone screening completed successfully!');
      if (onCompleted) {
        onCompleted();
      }
      if (onClose) {
        onClose();
      }
    } catch (error) {
      console.error('Failed to complete phone screening:', error);
      toastr.error(error.response?.data?.error || 'Failed to complete phone screening');
    } finally {
      setSubmitting(false);
    }
  };

  if (!screening) {
    return null;
  }

  return (
    <Drawer
      isOpen={true}
      onClose={onClose}
      title="Conduct Phone Screening"
      width="md"
      footer={
        <>
          <button
            type="button"
            className="drawer-btn drawer-btn-secondary"
            onClick={onClose}
            disabled={submitting}
          >
            Cancel
          </button>
          <button
            type="submit"
            className="drawer-btn drawer-btn-primary"
            onClick={handleComplete}
            disabled={submitting}
          >
            {submitting ? 'Saving...' : 'Complete Screening'}
          </button>
        </>
      }
    >
      <div style={{ marginBottom: '1.5rem' }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '1rem', marginBottom: '1rem' }}>
          <div>
            <div style={{ fontSize: '0.75rem', color: '#6B7280', marginBottom: '0.25rem' }}>Candidate</div>
            <div style={{ fontWeight: '500', color: '#111827' }}>
              {screening.jobApplicationId?.candidateInfo?.firstName} {screening.jobApplicationId?.candidateInfo?.lastName}
            </div>
          </div>
          <div>
            <div style={{ fontSize: '0.75rem', color: '#6B7280', marginBottom: '0.25rem' }}>Scheduled Time</div>
            <div style={{ fontWeight: '500', color: '#111827' }}>
              {new Date(screening.scheduledDate).toLocaleString()}
            </div>
          </div>
        </div>
      </div>

      {/* Screening Checklist */}
      <div style={{ marginBottom: '2rem' }}>
        <label style={{ display: 'block', marginBottom: '0.75rem', fontWeight: '600', color: '#111827' }}>
          Screening Checklist
        </label>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
          {checklist.map((item, index) => (
            <div key={index} style={{
              padding: '0.875rem',
              border: '1px solid #E5E7EB',
              borderRadius: '6px',
              background: '#FFFFFF'
            }}>
              <label style={{ display: 'flex', alignItems: 'flex-start', gap: '0.75rem', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={item.checked || false}
                  onChange={(e) => updateChecklistItem(index, 'checked', e.target.checked)}
                  style={{ marginTop: '0.25rem', cursor: 'pointer' }}
                />
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: '500', color: '#374151', marginBottom: '0.5rem' }}>
                    {item.question}
                  </div>
                  <textarea
                    value={item.notes || ''}
                    onChange={(e) => updateChecklistItem(index, 'notes', e.target.value)}
                    placeholder="Add notes..."
                    rows={2}
                    style={{
                      width: '100%',
                      padding: '0.5rem 0.75rem',
                      border: '1px solid #D1D5DB',
                      borderRadius: '4px',
                      fontSize: '0.875rem',
                      fontFamily: 'inherit',
                      resize: 'vertical'
                    }}
                  />
                </div>
              </label>
            </div>
          ))}
        </div>
      </div>

      {/* Screening Notes */}
      <div style={{ marginBottom: '2rem' }}>
        <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500', color: '#374151' }}>
          Screening Notes
        </label>
        <textarea
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          placeholder="Add overall notes about the screening call..."
          rows={4}
          style={{
            width: '100%',
            padding: '0.625rem 0.875rem',
            border: '1px solid #D1D5DB',
            borderRadius: '6px',
            fontSize: '0.875rem',
            fontFamily: 'inherit',
            resize: 'vertical'
          }}
        />
      </div>

      {/* Quick Rating */}
      <div style={{ marginBottom: '2rem' }}>
        <label style={{ display: 'block', marginBottom: '0.75rem', fontWeight: '600', color: '#111827' }}>
          Quick Rating
        </label>
        <div style={{ display: 'flex', gap: '0.75rem' }}>
          <button
            type="button"
            onClick={() => setRating(rating === 'thumbs_up' ? null : 'thumbs_up')}
            style={{
              flex: 1,
              padding: '1rem',
              border: rating === 'thumbs_up' ? '2px solid #10B981' : '1px solid #D1D5DB',
              borderRadius: '6px',
              cursor: 'pointer',
              background: rating === 'thumbs_up' ? '#D1FAE5' : '#FFFFFF',
              fontSize: '1.5rem',
              color: rating === 'thumbs_up' ? '#065F46' : '#6B7280'
            }}
            onMouseEnter={(e) => {
              if (rating !== 'thumbs_up') {
                e.target.style.borderColor = '#10B981';
                e.target.style.background = '#ECFDF5';
              }
            }}
            onMouseLeave={(e) => {
              if (rating !== 'thumbs_up') {
                e.target.style.borderColor = '#D1D5DB';
                e.target.style.background = '#FFFFFF';
              }
            }}
          >
            👍
          </button>
          <button
            type="button"
            onClick={() => setRating(rating === 'neutral' ? null : 'neutral')}
            style={{
              flex: 1,
              padding: '1rem',
              border: rating === 'neutral' ? '2px solid #6B7280' : '1px solid #D1D5DB',
              borderRadius: '6px',
              cursor: 'pointer',
              background: rating === 'neutral' ? '#F3F4F6' : '#FFFFFF',
              fontSize: '1.5rem',
              color: rating === 'neutral' ? '#374151' : '#6B7280'
            }}
            onMouseEnter={(e) => {
              if (rating !== 'neutral') {
                e.target.style.borderColor = '#6B7280';
                e.target.style.background = '#F9FAFB';
              }
            }}
            onMouseLeave={(e) => {
              if (rating !== 'neutral') {
                e.target.style.borderColor = '#D1D5DB';
                e.target.style.background = '#FFFFFF';
              }
            }}
          >
            😐
          </button>
          <button
            type="button"
            onClick={() => setRating(rating === 'thumbs_down' ? null : 'thumbs_down')}
            style={{
              flex: 1,
              padding: '1rem',
              border: rating === 'thumbs_down' ? '2px solid #EF4444' : '1px solid #D1D5DB',
              borderRadius: '6px',
              cursor: 'pointer',
              background: rating === 'thumbs_down' ? '#FEE2E2' : '#FFFFFF',
              fontSize: '1.5rem',
              color: rating === 'thumbs_down' ? '#991B1B' : '#6B7280'
            }}
            onMouseEnter={(e) => {
              if (rating !== 'thumbs_down') {
                e.target.style.borderColor = '#EF4444';
                e.target.style.background = '#FEF2F2';
              }
            }}
            onMouseLeave={(e) => {
              if (rating !== 'thumbs_down') {
                e.target.style.borderColor = '#D1D5DB';
                e.target.style.background = '#FFFFFF';
              }
            }}
          >
            👎
          </button>
        </div>
      </div>

      {/* Outcome */}
      <div style={{ marginBottom: '2rem' }}>
        <label style={{ display: 'block', marginBottom: '0.75rem', fontWeight: '600', color: '#111827' }}>
          Outcome
        </label>
        <div style={{ display: 'flex', gap: '0.75rem' }}>
          <label style={{
            flex: 1,
            padding: '0.75rem',
            border: outcome === 'pass' ? '2px solid #10B981' : '1px solid #D1D5DB',
            borderRadius: '6px',
            cursor: 'pointer',
            background: outcome === 'pass' ? '#D1FAE5' : '#FFFFFF',
            textAlign: 'center',
            fontWeight: outcome === 'pass' ? '600' : '400',
            color: outcome === 'pass' ? '#065F46' : '#374151'
          }}>
            <input
              type="radio"
              name="outcome"
              value="pass"
              checked={outcome === 'pass'}
              onChange={(e) => {
                setOutcome(e.target.value);
                setDecision('move_to_next_stage');
              }}
              style={{ display: 'none' }}
            />
            Pass
          </label>
          <label style={{
            flex: 1,
            padding: '0.75rem',
            border: outcome === 'fail' ? '2px solid #EF4444' : '1px solid #D1D5DB',
            borderRadius: '6px',
            cursor: 'pointer',
            background: outcome === 'fail' ? '#FEE2E2' : '#FFFFFF',
            textAlign: 'center',
            fontWeight: outcome === 'fail' ? '600' : '400',
            color: outcome === 'fail' ? '#991B1B' : '#374151'
          }}>
            <input
              type="radio"
              name="outcome"
              value="fail"
              checked={outcome === 'fail'}
              onChange={(e) => {
                setOutcome(e.target.value);
                setDecision('reject');
              }}
              style={{ display: 'none' }}
            />
            Fail
          </label>
        </div>
      </div>

      {/* Decision */}
      {outcome !== 'pending' && (
        <div style={{ marginBottom: '2rem' }}>
          <label style={{ display: 'block', marginBottom: '0.75rem', fontWeight: '600', color: '#111827' }}>
            Decision
          </label>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
            <label style={{
              padding: '0.75rem',
              border: decision === 'move_to_next_stage' ? '2px solid #10B981' : '1px solid #D1D5DB',
              borderRadius: '6px',
              cursor: 'pointer',
              background: decision === 'move_to_next_stage' ? '#D1FAE5' : '#FFFFFF',
              fontWeight: decision === 'move_to_next_stage' ? '600' : '400',
              color: decision === 'move_to_next_stage' ? '#065F46' : '#374151'
            }}>
              <input
                type="radio"
                name="decision"
                value="move_to_next_stage"
                checked={decision === 'move_to_next_stage'}
                onChange={(e) => setDecision(e.target.value)}
                style={{ display: 'none' }}
              />
              Move to Next Stage
            </label>
            
            {decision === 'move_to_next_stage' && (
              <div style={{
                padding: '0.75rem',
                background: '#F0F9FF',
                borderRadius: '6px',
                border: '1px solid #BFDBFE',
                marginLeft: '1.5rem'
              }}>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500', color: '#374151', fontSize: '0.875rem' }}>
                  Select Next Stage:
                </label>
                <select
                  value={nextStatus}
                  onChange={(e) => setNextStatus(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '0.625rem 0.875rem',
                    border: '1px solid #D1D5DB',
                    borderRadius: '6px',
                    fontSize: '0.875rem',
                    background: '#ffffff'
                  }}
                >
                  {validNextStatuses.map(status => (
                    <option key={status.value} value={status.value}>
                      {status.label}
                    </option>
                  ))}
                </select>
              </div>
            )}
            
            <label style={{
              padding: '0.75rem',
              border: decision === 'reject' ? '2px solid #EF4444' : '1px solid #D1D5DB',
              borderRadius: '6px',
              cursor: 'pointer',
              background: decision === 'reject' ? '#FEE2E2' : '#FFFFFF',
              fontWeight: decision === 'reject' ? '600' : '400',
              color: decision === 'reject' ? '#991B1B' : '#374151'
            }}>
              <input
                type="radio"
                name="decision"
                value="reject"
                checked={decision === 'reject'}
                onChange={(e) => setDecision(e.target.value)}
                style={{ display: 'none' }}
              />
              Reject Candidate
            </label>
          </div>
        </div>
      )}

      {/* Rejection Reason */}
      {decision === 'reject' && (
        <div style={{ marginBottom: '2rem' }}>
          <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500', color: '#374151' }}>
            Rejection Reason <span style={{ color: '#EF4444' }}>*</span>
          </label>
          <textarea
            value={rejectionReason}
            onChange={(e) => setRejectionReason(e.target.value)}
            placeholder="Please provide a reason for rejection..."
            rows={3}
            required
            style={{
              width: '100%',
              padding: '0.625rem 0.875rem',
              border: '1px solid #D1D5DB',
              borderRadius: '6px',
              fontSize: '0.875rem',
              fontFamily: 'inherit',
              resize: 'vertical'
            }}
          />
        </div>
      )}
    </Drawer>
  );
};

export default PhoneScreeningConduct;

